import {
  CostCenter,
  DocumentType,
  EndCustomer,
  OrderEntryDefaultAddressAutoFillType,
  PreviewPriceResult,
  PriceOverrideBreakdownItemInput,
  RecurrenceScheduleType,
  ServiceType,
} from "@api/graphql/generated/generated-types";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "@src/common/hooks/useAuth";
import { CustomerAction } from "../components/GeneralForm/GeneralForm";
import { EditableSurchargePricingRule } from "../components/SurchagePricingRulesForm";
import { OrderDetailsWidgetDataProps } from "@src/common/components/Widgets/OrderDetailsWidget";
import { DriverMapWidgetProps } from "@src/common/components/Widgets/DriverMapWidget";
import { getCourierTodayForDatePicker } from "@src/common/utils/timezoneConverters";
import { DEFAULT_INTERVAL_WEEKS } from "../components/EveryNWeeksIntervalInput";

export enum CustomRecurrenceScheduleType {
  DoesNotRepeat = "DOES_NOT_REPEAT",
}

export type ExtendedRecurrenceScheduleType =
  | RecurrenceScheduleType
  | CustomRecurrenceScheduleType;

export type Week = {
  Monday: boolean;
  Tuesday: boolean;
  Wednesday: boolean;
  Thursday: boolean;
  Friday: boolean;
  Saturday: boolean;
  Sunday: boolean;
};

interface ContextState {
  isNewCustomerFormOpen: boolean;
  setisNewCustomerFormOpen: (isOpen: boolean) => void;
  autoFillTypeValue: { label: string; id: string }[];
  setAutoFillTypeValue: (
    value: { label: CustomerAction; id: string }[]
  ) => void;
  selectedCustomer: EndCustomer | undefined;
  setSelectedCustomer: (customer: EndCustomer | undefined) => void;
  chosenCostCenter: CostCenter | undefined;
  setChosenCostCenter: (costCenter: CostCenter | undefined) => void;
  isCostCenterModalOpen: boolean;
  setIsCostCenterModalOpen: (isOpen: boolean) => void;
  selectedServiceType: ServiceType | undefined;
  setSelectedServiceType: (serviceType: ServiceType | undefined) => void;
  previewPrice: PreviewPriceResult | undefined;
  setPreviewPrice: (price: PreviewPriceResult | undefined) => void;
  surchargePricings: EditableSurchargePricingRule[];
  setSurchargePricings: (rows: EditableSurchargePricingRule[]) => void;
  // Replaces the calculated base price; undefined means no override.
  basePriceOverride: number | undefined;
  setBasePriceOverride: (amount: number | undefined) => void;
  // Replaces the whole price; undefined means no override.
  priceOverride: number | undefined;
  setPriceOverride: (amount: number | undefined) => void;
  priceOverrideBreakdownItems: PriceOverrideBreakdownItemInput[];
  setPriceOverrideBreakdownItems: (items: PriceOverrideBreakdownItemInput[]) => void;
  recurrenceSchedule: ExtendedRecurrenceScheduleType;
  setRecurrenceSchedule: (
    recurrenceSchedule: ExtendedRecurrenceScheduleType
  ) => void;
  selectedDaysWeek: Week;
  setSelectedDaysWeek: any;
  everyNWeeksInterval: number;
  setEveryNWeeksInterval: (weeks: number) => void;
  selectedCustomDaysOfTheMonth: any[];
  setSelectedCustomDaysOfTheMonth: (days: any[]) => void;
  scheduleDates: {
    startDate: Date | undefined;
    endDate: Date | null;
  };
  setScheduleDates: any;
  query: string;
  setQuery: (query: string) => void;
  orderDetails: OrderDetailsWidgetDataProps[];
  setOrderDetails: (orderDetails: OrderDetailsWidgetDataProps[]) => void;
  mapWidgetState: DriverMapWidgetProps | undefined;
  setMapWidgetState: (mapWidgetState: DriverMapWidgetProps | undefined) => void;
  selectedDuration: string;
  setSelectedDuration: (duration: string) => void;
  requiredDocTypes: DocumentType[];
  setRequiredDocTypes: (types: DocumentType[]) => void;
  warnedDocTypes: DocumentType[];
  setWarnedDocTypes: (types: DocumentType[]) => void;
}

const CreateOrderContext = createContext<ContextState | undefined>(undefined);

// Maps the courier's order-entry default address auto-fill setting to the
// initial customer-action selection. Defaults to "Pick Up at Customer" to
// preserve the prior hardcoded behavior.
function getDefaultAutoFillTypeValue(
  defaultAutoFillType: OrderEntryDefaultAddressAutoFillType | null | undefined
): { label: CustomerAction; id: string }[] {
  if (defaultAutoFillType === OrderEntryDefaultAddressAutoFillType.DropOff)
    return [{ label: CustomerAction.DropOffAtCustomer, id: "2" }];
  if (defaultAutoFillType === OrderEntryDefaultAddressAutoFillType.None)
    return [{ label: CustomerAction.None, id: "3" }];
  return [{ label: CustomerAction.PickUpAtCustomer, id: "1" }];
}

interface CreateOrderContextProviderProps {
  children: ReactNode;
}

export const CreateOrderContextProvider: React.FC<CreateOrderContextProviderProps> =
  ({ children }) => {
    const { courier } = useAuth();
    const [isNewCustomerFormOpen, setisNewCustomerFormOpen] = useState(false);
    const [autoFillTypeValue, setAutoFillTypeValue] = useState(
      getDefaultAutoFillTypeValue(
        courier?.courier?.orderEntryDefaultAddressAutoFillType
      )
    );
    // The provider can mount before `courier` resolves (auth loads async),
    // so the useState seed above may run with an undefined setting. Re-seed
    // once when the courier setting first becomes available. Guarded by a ref
    // so it never overwrites a dispatcher's in-session selection.
    const hasSeededAutoFillType = useRef(false);
    useEffect(() => {
      const defaultAutoFillType =
        courier?.courier?.orderEntryDefaultAddressAutoFillType;
      if (defaultAutoFillType && !hasSeededAutoFillType.current) {
        hasSeededAutoFillType.current = true;
        setAutoFillTypeValue(getDefaultAutoFillTypeValue(defaultAutoFillType));
      }
    }, [courier]);
    const [selectedCustomer, setSelectedCustomer] =
      useState<EndCustomer | undefined>(undefined);
    const [chosenCostCenter, setChosenCostCenter] =
      useState<CostCenter | undefined>(undefined);
    const [isCostCenterModalOpen, setIsCostCenterModalOpen] = useState(false);
    const [selectedServiceType, setSelectedServiceType] =
      useState<ServiceType | undefined>(undefined);
    const [previewPrice, setPreviewPrice] =
      useState<PreviewPriceResult | undefined>(undefined);
    const [surchargePricings, setSurchargePricings] = useState<
      EditableSurchargePricingRule[]
    >([]);
    const [basePriceOverride, setBasePriceOverride] =
      useState<number | undefined>(undefined);
    const [priceOverride, setPriceOverride] =
      useState<number | undefined>(undefined);
    const [priceOverrideBreakdownItems, setPriceOverrideBreakdownItems] =
      useState<PriceOverrideBreakdownItemInput[]>([]);
    const [recurrenceSchedule, setRecurrenceSchedule] =
      useState<ExtendedRecurrenceScheduleType>(
        CustomRecurrenceScheduleType.DoesNotRepeat
      );
    const [selectedDaysWeek, setSelectedDaysWeek] = React.useState({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
    });
    const [everyNWeeksInterval, setEveryNWeeksInterval] = useState<number>(
      DEFAULT_INTERVAL_WEEKS
    );
    const [selectedCustomDaysOfTheMonth, setSelectedCustomDaysOfTheMonth] =
      useState<any[]>([]);
    const [scheduleDates, setScheduleDates] = useState({
      startDate: getCourierTodayForDatePicker(),
      endDate: null,
    });
    const [query, setQuery] = useState("");
    const [orderDetails, setOrderDetails] = useState<
      OrderDetailsWidgetDataProps[]
    >([]);
    const [mapWidgetState, setMapWidgetState] =
      useState<DriverMapWidgetProps | undefined>(undefined);
    const [selectedDuration, setSelectedDuration] = useState("noStopDates");
    const [requiredDocTypes, setRequiredDocTypes] = useState<DocumentType[]>([]);
    const [warnedDocTypes, setWarnedDocTypes] = useState<DocumentType[]>([]);

    return (
      <CreateOrderContext.Provider
        value={{
          surchargePricings,
          setSurchargePricings,
          basePriceOverride,
          setBasePriceOverride,
          priceOverride,
          setPriceOverride,
          priceOverrideBreakdownItems,
          setPriceOverrideBreakdownItems,
          isNewCustomerFormOpen,
          setisNewCustomerFormOpen,
          autoFillTypeValue,
          setAutoFillTypeValue,
          selectedCustomer,
          setSelectedCustomer,
          chosenCostCenter,
          setChosenCostCenter,
          isCostCenterModalOpen,
          setIsCostCenterModalOpen,
          selectedServiceType,
          setSelectedServiceType,
          previewPrice,
          setPreviewPrice,
          recurrenceSchedule,
          setRecurrenceSchedule,
          selectedDaysWeek,
          setSelectedDaysWeek,
          everyNWeeksInterval,
          setEveryNWeeksInterval,
          selectedCustomDaysOfTheMonth,
          setSelectedCustomDaysOfTheMonth,
          scheduleDates,
          setScheduleDates,
          query,
          setQuery,
          orderDetails,
          setOrderDetails,
          mapWidgetState,
          setMapWidgetState,
          selectedDuration,
          setSelectedDuration,
          requiredDocTypes,
          setRequiredDocTypes,
          warnedDocTypes,
          setWarnedDocTypes,
        }}
      >
        {children}
      </CreateOrderContext.Provider>
    );
  };

export const useCreateOrderContext = () => {
  const context = useContext(CreateOrderContext);
  if (context === undefined) {
    throw new Error(
      "useCreateOrderContext must be used within a CreateOrderContextProvider"
    );
  }
  return context;
};
